import { IconButton } from '@/components/buttons/Buttons';
import Container, { Column } from '@/components/container/Container';
import Spacing from '@/components/container/Spacing';
import DropdownWithDatePicker from '@/components/form-inputs/DropdownWithDatePicker';
import CompanyAndHeading from '@/components/header/CompanyAndHeading';
import { HEADING } from '@/constants/insights-page';
import { PROGRAM } from '@/constants/labels';
import { REPORT_READY } from '@/constants/notification';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { MdCloudDownload } from 'react-icons/md';
import ActiveUsersSection from '../components/ActiveUsersSection';
import AverageSpendSection from '../components/AverageSpendSection';
import CompletedRequestVolumeSection from '../components/CompletedRequestVolumeSection';
import SpendingChartSection from '../components/SpendingChartSection';
import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { formatCurrency, GenericResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import moment from 'moment';
import { isAkDebug } from '@/common/DebugHelper';
import _ from 'lodash';
import DropdownInput from '@/components/inputs/DropdownInput';

export interface GetInsightsRequest {
	Start: Date;
	End: Date;
	GroupBy?: string;
	RangeName: string;
}

export interface InsightPointDto {
	Date: string;
	Value: number;
}

export interface Insights4SpendingDto {
	Spending: InsightPointDto[];
}
export interface Insights4VolumeDto {
	Volume: InsightPointDto[];
}
export interface InsightsOtherDto {
	ActiveUsers: number;
	AverageSpend: number;
}



type Option = {
	label: string;
	value: string | number;
};

type DateRange = {
	start: Date | null;
	end: Date | null;
};

const defaultDateOptions = [
	{ label: 'Today', value: 'today' },
	{ label: 'Last 7 days', value: 7 },
	{ label: 'Last 30 days', value: 30 },
	{ label: 'This month', value: 'this-month' },
	{ label: 'Custom Range', value: 'custom' },
];


const programOptions = [
	{ label: 'Travel Program', value: 'travel' },
	{ label: 'Business Program', value: 'business' },
];

const InsightsScreen = () => {
	const [dateRange, setDateRange] = useState<DateRange | null>({
		start: new Date(),
		end: new Date(),
	});
	const [date, setDate] = useState<string | number>('today');
	const [program, setProgram] = useState<string>(programOptions[0].value);
	const [expenseCode, setExpenseCode] = useState<string>('-------');
	const [spendViewMode, setSpendViewMode] = useState<'day' | 'week'>('day');
	const [completedViewMode, setCompletedViewMode] = useState<'day' | 'week'>(
		'day',
	);
	const insightsOtherQ = useQuery({
		queryKey: ['Insights.Spending', date, dateRange],
		queryFn: async () => {

			const result = await axiosInstance.post<GenericResponse<InsightsOtherDto>>(ENDPOINTS['/api/payments/GetInsightsOther'], { Start: dateRange?.start, End: dateRange?.end } as GetInsightsRequest);

			return result.data.Data;
		},
		enabled: !!(dateRange?.start && dateRange?.end),
	});


	useEffect(() => {
		if (date === 'custom') return; // Don't override custom date range

		let start: Date;
		let end: Date = new Date(); // End is always today for presets

		switch (date) {
			case 'today':
				start = moment().startOf('day').toDate();
				end = moment().endOf('day').toDate();
				break;
			case 7:
				start = moment().subtract(7, 'days').startOf('day').toDate();
				break;
			case 30:
				start = moment().subtract(30, 'days').startOf('day').toDate();
				break;
			case 'this-month':
				start = moment().startOf('month').toDate();
				break;
			default:
				return;
		}

		setDateRange({ start, end });
	}, [date]);

	const dateOptions: Option[] = defaultDateOptions;

	const selectedDateLabel =
		dateOptions.find((val) => val.value === date)?.label || '';

	const usersTable = [{ product: 'Business Travel', activeUsers: insightsOtherQ.data?.ActiveUsers ?? 0 }];
	const spendTable = [{ product: 'Business Travel', amount: formatCurrency(insightsOtherQ.data?.AverageSpend ?? 0) }];

	const formatRange = () => {
		if (
			date !== 'custom' ||
			!dateRange ||
			!dateRange.start ||
			!dateRange.end
		)
			return selectedDateLabel;
		return `from ${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`;
	};

	const title = formatRange();

	const handleExport = async () => {
		if (!dateRange?.start || !dateRange?.end) {
			return;
		}
		const groupBy = (date == 'today' ? 'hour' : (spendViewMode == 'day' ? 'day' : 'week'))
		const response = await axiosInstance.post(
			ENDPOINTS['/api/payments/ExportInsights'],
			{
				Start: dateRange.start.toISOString(),
				End: dateRange.end.toISOString(),
				GroupBy: groupBy,
				RangeName: title
			},
			{ responseType: 'blob' }
		);
		// Create a blob from the response
		const blob = new Blob([response.data], { type: 'application/pdf' });
		// Create a link element
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.setAttribute('download', 'insights.pdf');
		document.body.appendChild(link);
		link.click();
		link.parentNode?.removeChild(link);
		window.URL.revokeObjectURL(url);
	}
	const handleDebugExport = () => {
		// Open PDF viewer page in new tab with parameters
		if (!dateRange?.start || !dateRange?.end) {
			return;
		}
		const groupBy = (date == 'today' ? 'hour' : (spendViewMode == 'day' ? 'day' : 'week'))
		const params = new URLSearchParams({
			Start: dateRange.start.toISOString(),
			End: dateRange.end.toISOString(),
			GroupBy: groupBy,
			RangeName: title
		});

		window.open(`/insights-pdf?${params.toString()}`, '_blank');
	};

	// () => {

	// 	showNotification(REPORT_READY);
	// };

	return (
		<Container>
			<CompanyAndHeading heading={HEADING} />
			<Spacing height="h-5" />
			<div className="flex flex-col gap-5 md:flex-row md:justify-between tablet:flex-wrap">
				<div className="p-input-filled flex flex-col gap-5 md:flex-row tablet:flex-wrap">
					<DropdownWithDatePicker
						id={"InsightsDate"}
						date={date}
						setDate={setDate}
						options={dateOptions}
						dateRange={dateRange}
						setDateRange={setDateRange}
					/>
					<DropdownInput
						variant="outlined"
						id={PROGRAM}
						value={program}
						onChange={(e) => setProgram(e)}
						options={programOptions}
						label={PROGRAM}
						labelBgColor="bg-surface"
						containerCls="w-[260px]"
					/>
				</div>
				<div>
					<IconButton
						id="people-export"
						label="Export"
						icon={<MdCloudDownload className="mr-2" />}
						className="secondary h-[48px]"
						rounded
						onClick={handleExport}
					/>
					{isAkDebug() ? <IconButton
						id="people-export"
						label="Debug Export"
						icon={<MdCloudDownload className="mr-2" />}
						className="secondary h-[48px]"
						rounded
						onClick={handleDebugExport}
					/> : null}
				</div>
			</div>
			<Spacing height="h-6" />
			<Column gap="gap-6">
				<Container className="grid sm:grid-cols-1 md:grid-cols-2 tablet:grid-cols-1 gap-3 tablet:gap-2">
					<SpendingChartSection
						date={date}
						title={title}
						viewMode={spendViewMode}
						setViewMode={setSpendViewMode}
						dateRange={dateRange}
						program={program}
					/>
					<CompletedRequestVolumeSection date={date}
						title={title}
						viewMode={completedViewMode}
						setViewMode={setCompletedViewMode}
						dateRange={dateRange}
						program={program}
					/>
				</Container>
				<Container className="grid sm:grid-cols-1 md:grid-cols-2 tablet:grid-cols-1 gap-3 tablet:gap-2">
					<ActiveUsersSection
						hasData={insightsOtherQ.isSuccess}
						hasError={insightsOtherQ.isError}
						isLoaded={insightsOtherQ.isFetched}
						data={usersTable}
						title={title}
					/>
					<AverageSpendSection
						hasData={insightsOtherQ.isSuccess}
						hasError={insightsOtherQ.isError}
						isLoaded={insightsOtherQ.isFetched}
						data={spendTable}
					/>
				</Container>
			</Column>
			{/* //TODO: REMOVE */}
			<Spacing height="h-5" />
		</Container>
	);
};

export default InsightsScreen;
